import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid } from "@mui/material";
import { useState } from "react";
import { createEntityStore, UseEntityStore } from "../../common/entityStore";
import { propCustomEnumField, propNumberField } from "../../common/entityStoreForm";
import { propCurrency, propInt, propMaxValue, propMinValue, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { InventoryArticleDto } from "./types";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { createListStore, UseListStore } from "../../common/listStore";
import { SaleArticleDetailEditDto, SaleArticleDetailPackageItemEditDto } from "./SaleArticleDetailDialog";
import { newId } from "../../common/utils";
import { EnumItemDto } from "../../common/enum";
import { combineLatest, map } from "rxjs";
import { keyBy } from "lodash";

export const SaleArticleDetailPackageItemDialog = (props: {
    close: () => void;
    dialogProps: DialogProps;
    useStore: UseEntityStore<SaleArticleDetailEditDto>;
    packageItem: SaleArticleDetailPackageItemEditDto | null;
    inventoryArticle: InventoryArticleDto;
    useInventoryArticlesStore: UseListStore<InventoryArticleDto>;
}) => {
    const [useStore] = useState(() => createEntityStore<SaleArticleDetailPackageItemEditDto>({
        data: props.packageItem ?? {
            id: newId(),
            quantity: null,
            price: null,
        },
        onSave(item) {
            const packageItems = [...props.useStore.getState().clientData.packageItems];
            const existingIndex = packageItems.findIndex(i => i.saleArticleId === item.saleArticleId);
            if (existingIndex !== -1) {
                packageItems[existingIndex] = item;
            } else {
                packageItems.push(item);
            }
            props.useStore.getState().setClientDataProp("packageItems", packageItems);
            props.close();
        },
        propValidators: [
            propRequired("saleArticleId"),
            propValidValue("quantity", "quantityFormatted"),
            propRequired("quantity"),
            propMinValue("quantity", 1),
            propMaxValue("quantity", 100),
            propInt("quantity"),
            propValidValue("price", "priceFormatted"),
            propRequired("price"),
            propMinValue("price", 0),
            propMaxValue("price", 10_000),
            propCurrency("price"),
            propValueValidator("saleArticleId", p => props.useStore.getState().clientData.packageItems.some(i =>
                i.saleArticleId !== props.packageItem?.saleArticleId && i.saleArticleId === p) && "Article is already part of package."),
        ]
    }));
    const [useSaleArticlesStore] = useState(() => createListStore<EnumItemDto>({
        data: combineLatest([
            props.useInventoryArticlesStore.getState().items$,
            props.useStore.getState().clientData$.pipe(map(p => p.data.packageItems))
        ]).pipe(map(([inventoryArticles, packageItems]) => {
            const packageItem = props.packageItem;
            if (packageItem == null) {
                const existingInventoryArticleIds = keyBy(packageItems.map(i => i.inventoryArticleId).filter(id => id != null) as number[]);
                const existingSaleArticleIds = keyBy(packageItems.map(i => i.saleArticleId).filter(id => id != null) as number[]);
                const templateItems = keyBy(props.inventoryArticle.packageItems, p => p.inventoryArticleId);
                return {
                    items: inventoryArticles.items
                        .filter(p => templateItems[p.id] != null)
                        .filter(p =>
                            !existingInventoryArticleIds[p.id] &&
                            !p.saleArticles.some(p => existingSaleArticleIds[p.id]))
                        .flatMap(p => p.saleArticles),
                    isFetching: inventoryArticles.isFetching,
                };
            }

            const inventoryArticleId = packageItem?.saleArticleId != null ?
                inventoryArticles.items.find(p => p.saleArticles.some(p => p.id === packageItem.saleArticleId))?.id :
                packageItem.inventoryArticleId;
            var ias = inventoryArticleId != null ? inventoryArticles.items.filter(i => i.id === inventoryArticleId) : inventoryArticles.items;
            return { items: ias.flatMap(p => p.saleArticles), isFetching: inventoryArticles.isFetching };
        })),
    }));
    const { areSalesArticlesAvailable } = useSaleArticlesStore(p => ({ areSalesArticlesAvailable: p.items.length > 0 }));
    const { inventoryArticleName } = props.useInventoryArticlesStore(p => {
        const article = props.packageItem?.inventoryArticleId != null ? p.items.find(i => i.id === props.packageItem!.inventoryArticleId) : null;
        return { inventoryArticleName: article?.codeAndName };
    });
    return <Dialog {...props.dialogProps} maxWidth="xs"><Box m={2}>
        <DialogTitle>Edit package item</DialogTitle>
        <DialogContent>
            <Grid container spacing={2}>
                <Grid item xs={12}>{propCustomEnumField(useStore)("saleArticleId", "Article", useSaleArticlesStore, {
                    getId: p => p.id,
                    getName: p => p.code + " " + p.name,
                })}</Grid>
                <Grid item xs={12}>{propNumberField(useStore)("quantity", "quantityFormatted", "Quantity")}</Grid>
                <Grid item xs={12}>{propNumberField(useStore)("price", "priceFormatted", "Price", { isCurrency: true })}</Grid>
                {inventoryArticleName != null &&!areSalesArticlesAvailable && <Grid item xs={12}>
                    <Alert severity="warning">No sales articles available for {inventoryArticleName}. Enable sales article for this country in inventory article in warehouse app.</Alert>
                </Grid>}
            </Grid>
        </DialogContent>
        <DialogActions>
            <EntityStoreSaveButton useStore={useStore} />
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </Box></Dialog>;
};
